
import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, switchMap, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CREATE_CATEGORY, CREATE_PAYMENT_MODE, CREATE_SALE_CHANNEL,
  DELETE_CATEGORY, DELETE_PAYMENT_MODE, DELETE_SALE_CHANNEL,
  GET_ALL_CATEGORIES, GET_ALL_PAYMENT_MODES, GET_ALL_SALE_CHANNELS,
  GET_CATEGORY_BY_ID, GET_COMPANY_SETTINGS, GET_PAYMENT_MODE_BY_ID,
  GET_PUBLIC_COMPANY, GET_SALE_CHANNEL_BY_ID, UPDATE_CATEGORY,
  UPDATE_CATEGORY_STATUS, UPDATE_COMPANY_SETTINGS, UPDATE_PAYMENT_MODE,
  UPDATE_PAYMENT_MODE_STATUS, UPDATE_SALE_CHANNEL, UPDATE_SALE_CHANNEL_STATUS,
  UPLOAD_COMPANY_LOGO, GET_COMPANY_LOGO, DELETE_COMPANY_LOGO
} from '../../constant';
import { Category, CategoryRequest, UpdateCategoryStatusRequest } from '../../models/category.model';
import { PaymentModeConfig, PaymentModeRequest, UpdatePaymentModeStatusRequest } from '../../models/payment-mode.model';
import { SaleChannelConfig, SaleChannelRequest, UpdateSaleChannelStatusRequest } from '../../models/sale-channel.model';
import { CompanySettings, CompanySettingsRequest } from '../../models/invoice.model';

export interface PublicCompanyIdentity {
  companyName: string;
  logoAvailable: boolean;
  logoUrl: string | null;
}

@Injectable({ providedIn: 'root' })
export class SettingsService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly server_url = environment.apiUrl;

  private readonly publicCompanyState = signal<PublicCompanyIdentity | null>(null);
  readonly publicCompany = this.publicCompanyState.asReadonly();
  readonly publicCompanyName = computed(() => this.publicCompanyState()?.companyName ?? '');
  private readonly publicLogoVersion = signal(0);

  readonly publicLogoUrl = computed(() => {
    const c = this.publicCompanyState();
    if (!c?.logoAvailable || !c.logoUrl) return null;
    const url = /^https?:\/\//.test(c.logoUrl) ? c.logoUrl : this.server_url + (c.logoUrl.startsWith('/') ? '' : '/') + c.logoUrl;
    return url + (url.includes('?') ? '&' : '?') + 'v=' + this.publicLogoVersion();
  });

  private readonly companyState = signal<CompanySettings | null>(null);
  readonly companySettings = this.companyState.asReadonly();
  readonly companyName = computed(() => this.companyState()?.companyName ?? this.publicCompanyState()?.companyName ?? '');

  private readonly logoState = signal<string | null>(null);
  readonly companyLogoUrl = this.logoState.asReadonly();
  private logoObjectUrl: string | null = null;

  private setLogo(blob: Blob | null): void {
    const previous = this.logoObjectUrl;
    const next = blob?.size ? URL.createObjectURL(blob) : null;
    this.logoObjectUrl = next;
    this.logoState.set(next);
    if (previous) URL.revokeObjectURL(previous);
  }

  refreshCompanyLogo(): Observable<Blob | null> {
    return this.getCompanyLogo().pipe(
      tap(blob => this.setLogo(blob)),
      map(blob => blob as Blob | null),
      catchError(err => {
        if (err.status !== 404) console.error('Chargement du logo impossible.', err);
        this.setLogo(null);
        return of(null);
      })
    );
  }

  ngOnDestroy(): void {
    if (this.logoObjectUrl) URL.revokeObjectURL(this.logoObjectUrl);
  }

  /* CATÉGORIES */
  getAllCategories(): Observable<Category[]> {
    return this.http.get<Category[]>(this.server_url + GET_ALL_CATEGORIES);
  }
  getCategoryById(id: number): Observable<Category> {
    return this.http.get<Category>(this.server_url + GET_CATEGORY_BY_ID + id);
  }
  createCategory(request: CategoryRequest): Observable<Category> {
    return this.http.post<Category>(this.server_url + CREATE_CATEGORY, request);
  }
  updateCategory(id: number, request: CategoryRequest): Observable<Category> {
    return this.http.put<Category>(this.server_url + UPDATE_CATEGORY + id, request);
  }
  updateCategoryStatus(id: number, request: UpdateCategoryStatusRequest): Observable<Category> {
    return this.http.patch<Category>(this.server_url + UPDATE_CATEGORY_STATUS + id + '/status', request);
  }
  deleteCategory(id: number): Observable<void> {
    return this.http.delete<void>(this.server_url + DELETE_CATEGORY + id);
  }

  /* MODES DE PAIEMENT */
  getAllPaymentModes(): Observable<PaymentModeConfig[]> {
    return this.http.get<PaymentModeConfig[]>(this.server_url + GET_ALL_PAYMENT_MODES);
  }
  getPaymentModeById(id: number): Observable<PaymentModeConfig> {
    return this.http.get<PaymentModeConfig>(this.server_url + GET_PAYMENT_MODE_BY_ID + id);
  }
  createPaymentMode(request: PaymentModeRequest): Observable<PaymentModeConfig> {
    return this.http.post<PaymentModeConfig>(this.server_url + CREATE_PAYMENT_MODE, request);
  }
  updatePaymentMode(id: number, request: PaymentModeRequest): Observable<PaymentModeConfig> {
    return this.http.put<PaymentModeConfig>(this.server_url + UPDATE_PAYMENT_MODE + id, request);
  }
  updatePaymentModeStatus(id: number, request: UpdatePaymentModeStatusRequest): Observable<PaymentModeConfig> {
    return this.http.patch<PaymentModeConfig>(this.server_url + UPDATE_PAYMENT_MODE_STATUS + id + '/status', request);
  }
  deletePaymentMode(id: number): Observable<void> {
    return this.http.delete<void>(this.server_url + DELETE_PAYMENT_MODE + id);
  }

  /* CANAUX DE VENTE */
  getAllSaleChannels(): Observable<SaleChannelConfig[]> {
    return this.http.get<SaleChannelConfig[]>(this.server_url + GET_ALL_SALE_CHANNELS);
  }
  getSaleChannelById(id: number): Observable<SaleChannelConfig> {
    return this.http.get<SaleChannelConfig>(this.server_url + GET_SALE_CHANNEL_BY_ID + id);
  }
  createSaleChannel(request: SaleChannelRequest): Observable<SaleChannelConfig> {
    return this.http.post<SaleChannelConfig>(this.server_url + CREATE_SALE_CHANNEL, request);
  }
  updateSaleChannel(id: number, request: SaleChannelRequest): Observable<SaleChannelConfig> {
    return this.http.put<SaleChannelConfig>(this.server_url + UPDATE_SALE_CHANNEL + id, request);
  }
  updateSaleChannelStatus(id: number, request: UpdateSaleChannelStatusRequest): Observable<SaleChannelConfig> {
    return this.http.patch<SaleChannelConfig>(this.server_url + UPDATE_SALE_CHANNEL_STATUS + id + '/status', request);
  }
  deleteSaleChannel(id: number): Observable<void> {
    return this.http.delete<void>(this.server_url + DELETE_SALE_CHANNEL + id);
  }

  /* ENTREPRISE */
  getCompanySettings(): Observable<CompanySettings> {
    return this.http.get<CompanySettings>(this.server_url + GET_COMPANY_SETTINGS).pipe(
      tap(company => this.companyState.set(company))
    );
  }
  updateCompanySettings(request: CompanySettingsRequest): Observable<CompanySettings> {
    return this.http.put<CompanySettings>(this.server_url + UPDATE_COMPANY_SETTINGS, request).pipe(
      tap(company => {
        this.companyState.set(company);
        this.publicCompanyState.update(c => c ? { ...c, companyName: company.companyName } : c);
      })
    );
  }

  /* IDENTITÉ PUBLIQUE */
  getPublicCompany(): Observable<PublicCompanyIdentity> {
    return this.http.get<PublicCompanyIdentity>(this.server_url + GET_PUBLIC_COMPANY).pipe(
      tap(company => this.publicCompanyState.set(company))
    );
  }

  /* LOGO */
  uploadCompanyLogo(file: File): Observable<void> {
    const data = new FormData();
    data.append('file', file);
    return this.http.post<void>(this.server_url + UPLOAD_COMPANY_LOGO, data).pipe(
      switchMap(() => {
        this.companyState.update(c => c ? { ...c, logoAvailable: true } : c);
        this.publicCompanyState.update(c => c ? { ...c, logoAvailable: true } : c);
        this.publicLogoVersion.update(v => v + 1);
        return this.refreshCompanyLogo().pipe(map(() => void 0));
      })
    );
  }
  getCompanyLogo(): Observable<Blob> {
    return this.http.get(this.server_url + GET_COMPANY_LOGO, { responseType: 'blob' });
  }
  deleteCompanyLogo(): Observable<void> {
    return this.http.delete<void>(this.server_url + DELETE_COMPANY_LOGO).pipe(
      tap(() => {
        this.companyState.update(c => c ? { ...c, logoAvailable: false, logoUrl: null } : c);
        this.publicCompanyState.update(c => c ? { ...c, logoAvailable: false, logoUrl: null } : c);
        this.publicLogoVersion.update(v => v + 1);
        this.setLogo(null);
      })
    );
  }
}
